/**
 * i18n configuration — react-i18next with EU languages.
 * Import this module once in main.tsx: `import './i18n/config.js'`
 *
 * All 8 EU locales fully translated: en, de, fr, nl, pl, sv, no, da
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { pickLanguage, storedLanguage } from './detect';

// Static imports of all locale bundles (tree-shaken in production per locale)
import en from './locales/en.json';
import de from './locales/de.json';
import fr from './locales/fr.json';
import nl from './locales/nl.json';
import pl from './locales/pl.json';
import sv from './locales/sv.json';
import no from './locales/no.json';
import da from './locales/da.json';

/** All 8 EU locales — fully translated and available in the language switcher. */
export const SUPPORTED_LOCALES: { code: string; label: string; flag: string }[] = [
  { code: 'en', label: 'English',    flag: '🇬🇧' },
  { code: 'de', label: 'Deutsch',    flag: '🇩🇪' },
  { code: 'fr', label: 'Français',   flag: '🇫🇷' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  { code: 'pl', label: 'Polski',     flag: '🇵🇱' },
  { code: 'sv', label: 'Svenska',    flag: '🇸🇪' },
  { code: 'no', label: 'Norsk',      flag: '🇳🇴' },
  { code: 'da', label: 'Dansk',      flag: '🇩🇰' },
];

const LANGUAGE_KEY = 'eurion-language';
const CODES = SUPPORTED_LOCALES.map(l => l.code);
const browserLanguages = typeof navigator === 'undefined' ? [] : (navigator.languages?.length ? navigator.languages : [navigator.language]);

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      de: { translation: de },
      fr: { translation: fr },
      nl: { translation: nl },
      pl: { translation: pl },
      sv: { translation: sv },
      no: { translation: no },
      da: { translation: da },
    },
    // The user's saved choice, else the first browser language we support (detect.ts)
    lng: storedLanguage(LANGUAGE_KEY, CODES) ?? pickLanguage(browserLanguages, CODES),
    fallbackLng: 'en',
    supportedLngs: CODES,
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: {
      // Only used to save the language when the user changes it
      order:  ['localStorage'],
      caches: ['localStorage'],
      lookupLocalStorage: LANGUAGE_KEY,
    },
  });

export default i18n;
